import { chargeBaseTakesSeats } from "../charge-base";
import type {
  PackageCategoryDto,
  PackageDto,
} from "../../dtos/traveler/tour.dto";

/** Map of category ID → selected count. Held in FE state; consumed by the
 *  math functions below. Mirrors the shape used by the booking widget. */
export type TravelersState = Readonly<Record<string, number>>;

/** Unit price (minor units) a category charges at a given count.
 *
 *  - `flat`: always `priceMinor`.
 *  - `tiered`: tier whose `[from, to]` window contains `count`. Falls through
 *    to `flat.now.amount` if no tier matches — never NaN, never undefined.
 *
 *  Single source of truth: callers must not branch on `chargeBase`. Whether
 *  the unit serves 1 person, 2 (Couple), or 4 (Cabin) is irrelevant to
 *  *money*; that conversion lives in {@link paxFor}. */
export function resolveCategoryUnitPriceMinor(
  cat: PackageCategoryDto,
  count: number,
): number {
  if (cat.strategy === "tiered" && cat.tiers?.length) {
    const tier = cat.tiers.find(
      (t) => count >= t.from && (t.to === null || count <= t.to),
    );
    if (tier) return tier.price.amount;
  }
  return cat.flat?.now.amount ?? 0;
}

/** Original (pre-discount) unit price (minor units).
 *
 *  Tiered categories always compare against the **first tier**'s `was`
 *  (falling through to its `price` when no `was` is set). Per-tier `was`
 *  on higher tiers is no longer authored — group savings are expressed as
 *  the gap between the first tier and the matched tier. Flat categories
 *  use `flat.was ?? flat.now`. */
export function resolveCategoryOriginalUnitPriceMinor(
  cat: PackageCategoryDto,
): number {
  if (cat.strategy === "tiered" && cat.tiers?.length) {
    const baseTier =
      cat.tiers.find((t) => 1 >= t.from && (t.to === null || 1 <= t.to)) ??
      cat.tiers[0]!;
    return baseTier.was?.amount ?? baseTier.price.amount;
  }
  return cat.flat?.was?.amount ?? cat.flat?.now.amount ?? 0;
}

/** Live total (minor units) = Σ count × unitPrice over categories with
 *  count > 0. Categories with count = 0 contribute nothing — there is no
 *  flat-fee auto-add. (Mandatory fees, if ever introduced, will live as a
 *  separate booking-level surcharge, not as a price category.) */
export function liveTotalMinor(
  categories: readonly PackageCategoryDto[],
  travelers: TravelersState,
): number {
  return categories.reduce((sum, cat) => {
    const count = travelers[cat.id] ?? 0;
    if (count <= 0) return sum;
    return sum + count * resolveCategoryUnitPriceMinor(cat, count);
  }, 0);
}

/** Live original (pre-discount) total in minor units. */
export function liveOriginalTotalMinor(
  categories: readonly PackageCategoryDto[],
  travelers: TravelersState,
): number {
  return categories.reduce((sum, cat) => {
    const count = travelers[cat.id] ?? 0;
    if (count <= 0) return sum;
    return sum + count * resolveCategoryOriginalUnitPriceMinor(cat);
  }, 0);
}

/** Project a (category, count) tuple into *people*:
 *   - `chargeBase=person` ⇒ `unitCapacity` is null ⇒ 1 person per count
 *   - `chargeBase=unit | group` ⇒ `count × unitCapacity` people
 *
 *   - `chargeBase=asset` ⇒ 0 people: a rented item takes no seat
 *
 *  Used by the booking widget to compute the "Total Travelers" pill and to
 *  enforce per-departure capacity. Adding +1 Couple (unitCapacity=2) adds
 *  +2 to the pill — not +1. */
export function paxFor(cat: PackageCategoryDto, count: number): number {
  if (!chargeBaseTakesSeats(cat.chargeBase)) return 0;
  return count * (cat.unitCapacity ?? 1);
}

/** Seed traveler counts for first paint. The operator-flagged default
 *  category gets count 1; every other category gets 0.
 *
 *  Falls back to the first category in the package when no `isDefault`
 *  flag is set — the server is expected (Phase 3 invariant) to guarantee
 *  exactly one default per package, so this branch only fires for legacy
 *  or partial data. */
export function seedTravelersForPackage(pkg: PackageDto): TravelersState {
  const seedId =
    pkg.categories.find((c) => c.isDefault)?.id ?? pkg.categories[0]?.id;
  const state: Record<string, number> = {};
  for (const cat of pkg.categories) {
    state[cat.id] = cat.id === seedId ? 1 : 0;
  }
  return state;
}
